import React, { memo, useMemo } from "react";
import type { CommercialConfig } from "./commercialConfig";
import {
  formatDemoDocumentNumber,
  getDevise,
  getDisplayAddress,
  getDisplayCompanyName,
  getLogoUrl,
  getPiedPageFacture,
  getSignatureImageUrl,
} from "./commercialConfig";
import { formatMoney } from "./formatMoney";

export type ProformaPreviewProps = {
  config: CommercialConfig;
};

const DEMO_TOTAL = 1180;

function ProformaPreviewInner({ config }: ProformaPreviewProps): React.JSX.Element {
  const cur = getDevise(config);
  const title = getDisplayCompanyName(config);
  const addr = getDisplayAddress(config);
  const logo = getLogoUrl(config);
  const sig = getSignatureImageUrl(config);
  const footer = getPiedPageFacture(config);

  const docNo = useMemo(
    () => formatDemoDocumentNumber(config, "proforma"),
    [config]
  );

  const validite = useMemo(() => {
    const j = config.validiteProformaJours;
    const days = j != null && Number.isFinite(j) && j >= 0 ? j : 14;
    const t = new Date();
    t.setDate(t.getDate() + days);
    return t.toLocaleDateString("fr-FR");
  }, [config.validiteProformaJours]);

  const bankLines = [
    config.banque?.trim() && `Banque : ${config.banque}`,
    config.iban?.trim() && `IBAN : ${config.iban}`,
    config.swift?.trim() && `SWIFT : ${config.swift}`,
    config.mobileMoneyEntreprise?.trim() && `Mobile money : ${config.mobileMoneyEntreprise}`,
  ].filter(Boolean) as string[];

  return (
    <>
      <div className="flex justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          {logo ? (
            <img src={logo} alt="" className="mb-2 h-10 max-w-[120px] object-contain" />
          ) : null}
          <p className="text-sm font-bold">{title}</p>
          {config.slogan?.trim() ? <p className="text-[10px] text-slate-600">{config.slogan}</p> : null}
          <div className="mt-2 text-[10px] leading-relaxed text-slate-600">
            {addr ? <p className="whitespace-pre-line">{addr}</p> : null}
            {config.telephone?.trim() ? <p>Tél. {config.telephone}</p> : null}
            {config.email?.trim() ? <p>{config.email}</p> : null}
            <p>
              RCCM : {config.rccm?.trim() || "—"} · NIF : {config.nif?.trim() || "—"} · ID NAT :{" "}
              {config.idNat?.trim() || "—"}
            </p>
          </div>
        </div>
        <div className="text-right text-[10px]">
          <p className="text-base font-bold text-slate-900">PROFORMA</p>
          <p className="font-mono">{docNo}</p>
          <p>Validité jusqu’au : {validite}</p>
        </div>
      </div>
      <p className="mt-4 text-[10px] text-slate-700">
        <span className="font-semibold">Client : </span>Client démo — Professionnel
      </p>
      <p className="mt-2 text-[10px]">
        <span className="font-semibold">Type de prestation : </span>Collecte & traitement déchets
      </p>
      <table className="mt-4 w-full border-collapse text-[10px]">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="py-1.5 text-left">Libellé</th>
            <th className="py-1.5 text-right">Montant</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-100">
            <td className="py-2">Forfait selon devis</td>
            <td className="text-right">{formatMoney(DEMO_TOTAL, cur)}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-4 text-right text-sm font-bold">Total TTC : {formatMoney(DEMO_TOTAL, cur)}</p>
      {bankLines.length > 0 ? (
        <div className="mt-6 border-t border-slate-200 pt-3 text-[10px] text-slate-700">
          <p className="font-semibold text-slate-800">Coordonnées bancaires</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {bankLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {config.conditionsPaiement?.trim() ? (
        <div className="mt-4 text-[10px] text-slate-700">
          <p className="font-semibold">Conditions de paiement</p>
          <p className="mt-1 whitespace-pre-line">{config.conditionsPaiement}</p>
        </div>
      ) : null}
      {config.mentionProforma?.trim() ? (
        <p className="mt-4 whitespace-pre-line text-[9px] text-slate-600">{config.mentionProforma}</p>
      ) : null}
      {footer ? (
        <p className="mt-4 whitespace-pre-line text-[9px] text-slate-500">{footer}</p>
      ) : null}
      <p className="mt-6 rounded-md bg-amber-50 px-3 py-2 text-[10px] font-medium text-amber-900 ring-1 ring-amber-200">
        Cette proforma ne vaut pas facture.
      </p>
      <div className="mt-6 flex justify-end">
        <div className="text-center text-[9px] text-slate-500">
          {sig ? (
            <img src={sig} alt="" className="mx-auto h-14 object-contain" />
          ) : (
            <div className="mx-auto h-14 w-28 rounded border border-dashed border-slate-200" />
          )}
          <p className="mt-1">{config.nomSignataire?.trim() || "Signature"}</p>
          {config.fonctionSignataire?.trim() ? (
            <p className="text-[8px] text-slate-500">{config.fonctionSignataire}</p>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        disabled
        className="mt-4 w-full cursor-not-allowed rounded-lg bg-slate-200 py-2 text-[11px] font-medium text-slate-400"
      >
        Convertir en facture (aperçu)
      </button>
    </>
  );
}

export const ProformaPreview = memo(ProformaPreviewInner);
